import React, { useState, useEffect } from 'react';
import useModalHistory from '../hooks/useModalHistory';
import { X, TrendingUp, TrendingDown, AlertTriangle, Sparkles, Info } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useTour } from '../tour/TourContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import SentimentStrip from './SentimentStrip';
import { formatPrice } from '../utils/formatters';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Trade Reason Options (Required - Single Select)
const TRADE_REASONS = [
  { id: 'strategy', label: 'Strategy', icon: '📊' },
  { id: 'scanner', label: 'Scanner', icon: '🔍' },
  { id: 'technical', label: 'Technical', icon: '📈' },
  { id: 'fundamental', label: 'Fundamental', icon: '📑' },
  { id: 'sentiment', label: 'Sentiment', icon: '📰' },
  { id: 'news', label: 'News/Event', icon: '🗞️' },
  { id: 'tip', label: 'Tip', icon: '💡' },
  { id: 'impulse', label: 'Impulse', icon: '⚡' },
];

// Technical Tags (Optional - Multi Select)
const TECHNICAL_TAGS = [
  { id: 'breakout', label: 'Breakout' },
  { id: 'rsi', label: 'RSI' },
  { id: 'macd', label: 'MACD' },
  { id: 'volume', label: 'Volume Spike' },
  { id: 'moving_avg', label: 'Moving Average' },
  { id: 'gap', label: 'Gap Up/Down' },
  { id: 'support', label: 'Support' },
  { id: 'resistance', label: 'Resistance' },
];

// Parse strategy_tags array back into trade reason + technical tag ids
function parseStrategyTags(tags = []) {
  const reasonIds = new Set(TRADE_REASONS.map(r => r.id));
  const techIds = new Set(TECHNICAL_TAGS.map(t => t.id));
  let reason = '';
  const tech = [];
  for (const t of tags) {
    if (reasonIds.has(t)) reason = t;
    else if (techIds.has(t)) tech.push(t);
  }
  return { reason, tech };
}

function OrderModal({ isOpen, onClose, stock, orderType = 'BUY', editOrder = null, onOrderUpdated, initialValues = null }) {
  const { colors } = useTheme();
  const { user } = useAuth();
  const { isRunning: isTourRunning } = useTour();
  const isEditMode = !!editOrder;
  const isFO = !!stock?.contract_id || !!stock?.is_fo;
  const lotSize = stock?.lot_size || 1;
  const currentPrice = stock?.current_price || stock?.ltp || 0;
  const displaySymbol = stock?.symbol ? stock.symbol.replace('.NS', '') : '';
  const quantityLabel = isFO ? 'Lots' : 'Quantity';

  // Existing trade fields
  const [type, setType] = useState(orderType);
  const [productType, setProductType] = useState('SWING'); // INTRADAY | SWING
  const [quantity, setQuantity] = useState(1);
  const [orderMode, setOrderMode] = useState('MARKET');
  const [limitPrice, setLimitPrice] = useState('');
  const [stopLoss, setStopLoss] = useState('');
  const [target, setTarget] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Trade reasoning fields
  const [tradeReason, setTradeReason] = useState('');
  const [technicalTags, setTechnicalTags] = useState([]);
  const [confidence, setConfidence] = useState(5);
  const [journalNotes, setJournalNotes] = useState('');
  const [reasonError, setReasonError] = useState(false);

  // AI quality score
  const [qualityScore, setQualityScore] = useState(null);
  const [qualityLoading, setQualityLoading] = useState(false);

  // Risk profile
  const [riskProfile, setRiskProfile] = useState(null);
  const [todayTradeCount, setTodayTradeCount] = useState(0);
  const [showRiskPopup, setShowRiskPopup] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    if (isEditMode && editOrder) {
      // Pre-fill from existing order
      const { reason, tech } = parseStrategyTags(editOrder.strategy_tags || []);
      setType(editOrder.order_type || editOrder.trade_type || 'BUY');
      setProductType(editOrder.product_type || (editOrder.order_variant === 'INTRADAY' ? 'INTRADAY' : 'SWING'));
      setOrderMode('LIMIT');
      setQuantity(editOrder.quantity || 1);
      setLimitPrice(editOrder.limit_price != null ? Number(editOrder.limit_price).toString() : '');
      setStopLoss(editOrder.stop_loss_price != null ? Number(editOrder.stop_loss_price).toString() : '');
      setTarget(editOrder.target_price != null ? Number(editOrder.target_price).toString() : '');
      setTradeReason(reason);
      setTechnicalTags(tech);
      setConfidence(5);
      setJournalNotes(editOrder.notes || '');
      setShowAdvanced(!!editOrder.notes);
      setReasonError(false);
      setQualityScore(null);
    } else {
      // New order — reset, optionally pre-fill from initialValues (e.g. pattern trade)
      setType(initialValues?.orderType || orderType);
      setProductType(initialValues?.productType || 'SWING');
      setOrderMode(initialValues?.limitPrice ? 'LIMIT' : 'MARKET');
      setQuantity(initialValues?.quantity || 1);
      setLimitPrice(initialValues?.limitPrice?.toFixed(2) || currentPrice?.toFixed(2) || '');
      setStopLoss(initialValues?.stopLoss?.toFixed(2) || '');
      setTarget(initialValues?.target?.toFixed(2) || '');
      setTradeReason(initialValues?.tradeReason || '');
      setTechnicalTags(initialValues?.technicalTags || []);
      setConfidence(5);
      setJournalNotes(initialValues?.notes || '');
      setShowAdvanced(!!(initialValues?.notes));
      setReasonError(false);
      setQualityScore(null);
    }
  }, [isOpen, isEditMode, editOrder?.order_id]);

  // Fetch risk profile + today trade count on open
  useEffect(() => {
    if (!isOpen) return;
    axios.get(`${API_URL}/auth/profile`).then(r => setRiskProfile(r.data && Object.keys(r.data).length ? r.data : null)).catch(() => {});
    axios.get(`${API_URL}/trade/today-count`).then(r => setTodayTradeCount(r.data.count || 0)).catch(() => {});
  }, [isOpen]);

  // Escape key closes modal
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  useModalHistory(isOpen, onClose);

  // Auto-fetch quality score when trade reason is selected
  useEffect(() => {
    if (!tradeReason || !stock || !isOpen) return;
    let cancelled = false;
    const fetch = async () => {
      setQualityLoading(true);
      try {
        const res = await axios.post(`${API_URL}/ai/trade-quality-score`, {
          symbol: stock.symbol,
          order_type: type,
          quantity: unitQuantity,
          price: currentPrice || 0,
          stop_loss: stopLoss ? parseFloat(stopLoss) : null,
          target: target ? parseFloat(target) : null,
          trade_reason: tradeReason,
          technical_tags: technicalTags,
        });
        if (!cancelled) setQualityScore(res.data);
      } catch (e) {
        // fail silently
      } finally {
        if (!cancelled) setQualityLoading(false);
      }
    };
    fetch();
    return () => { cancelled = true; };
  }, [tradeReason]);

  if (!isOpen || !stock) return null;

  const unitQuantity = isFO ? quantity * lotSize : quantity;
  const totalValue = currentPrice * unitQuantity;

  // F&O instrument type: options (CE/PE) vs futures (FUT)
  const isOption = isFO && (stock?.symbol?.endsWith('CE') || stock?.symbol?.endsWith('PE'));
  const isFuture = isFO && !isOption && stock?.symbol?.includes('FUT');

  // For futures, capital reserved is margin (10% of notional) not full notional.
  // Real NSE SPAN margin is ~15%; we use 10% here so beginners can practise with
  // the ₹10L virtual account without being blocked by margin requirements.
  const FUT_MARGIN_RATE = 0.10;
  const capitalRequired = isFuture ? totalValue * FUT_MARGIN_RATE : totalValue;

  // Risk panel calculations (BUY only)
  const portfolioBalance = user?.active_capital || 100000;
  const recommendedMaxPct = riskProfile?.recommended_risk_percent ?? null;
  const recommendedMaxLoss = recommendedMaxPct ? (portfolioBalance * recommendedMaxPct / 100) : null;
  // Entry price: use limit price when in LIMIT mode, else live price
  const entryPrice = (orderMode === 'LIMIT' && limitPrice) ? parseFloat(limitPrice) : currentPrice;
  const slPrice = stopLoss ? parseFloat(stopLoss) : null;
  const isBuy = type === 'BUY';
  // For options: full premium paid is at risk regardless of SL (options can gap to ₹0).
  // For equity/futures: risk is the SL-based delta.
  const premiumAtRisk = (isOption && entryPrice && unitQuantity) ? entryPrice * unitQuantity : null;
  const impliedLossAmount = isOption
    ? premiumAtRisk
    : (slPrice && entryPrice && unitQuantity &&
        ((isBuy && slPrice < entryPrice) || (!isBuy && slPrice > entryPrice)))
      ? Math.abs(entryPrice - slPrice) * unitQuantity : null;
  const impliedLossPct = (impliedLossAmount && portfolioBalance > 0)
    ? (impliedLossAmount / portfolioBalance) * 100 : null;
  // Futures notional exposure
  const notionalExposure = (isFuture && entryPrice && unitQuantity) ? entryPrice * unitQuantity : null;
  const notionalMultiple = (notionalExposure && portfolioBalance > 0)
    ? (notionalExposure / portfolioBalance) : null;
  // Suggested SL — only meaningful for equity/futures (not options where premium is already the risk)
  const suggestedSL = (!isOption && recommendedMaxLoss && entryPrice && unitQuantity > 0)
    ? (isBuy ? (entryPrice - recommendedMaxLoss / unitQuantity) : (entryPrice + recommendedMaxLoss / unitQuantity))
    : null;
  // Conservative/Very Conservative profiles not well-suited for F&O
  const profileType = riskProfile?.risk_profile_type || '';
  const foProfileWarning = isFO && recommendedMaxPct &&
    (profileType === 'Conservative' || profileType === 'Very Conservative');
  const riskSeverity = !recommendedMaxPct ? null
    : isOption
      ? (!impliedLossPct ? null : impliedLossPct <= recommendedMaxPct ? 'ok' : impliedLossPct <= recommendedMaxPct * 2 ? 'warn' : 'danger')
      : !impliedLossPct ? 'no-sl'
      : impliedLossPct <= recommendedMaxPct ? 'ok'
      : impliedLossPct <= recommendedMaxPct * 2 ? 'warn' : 'danger';
  const riskBarColor = riskSeverity === 'ok' ? '#16C784' : riskSeverity === 'warn' ? '#f97316' : '#ef4444';
  const riskBarWidth = (recommendedMaxPct && impliedLossPct)
    ? Math.min((impliedLossPct / (recommendedMaxPct * 2)) * 100, 100) : 0;
  const styleMismatch = riskProfile?.recommended_trade_style &&
    productType === 'INTRADAY' &&
    riskProfile.recommended_trade_style !== 'Intraday' &&
    riskProfile.recommended_trade_style !== 'Intraday / Swing';
  const atTradeLimit = riskProfile?.recommended_max_trades &&
    todayTradeCount >= riskProfile.recommended_max_trades;

  // Toggle technical tag selection
  const toggleTag = (tagId) => {
    setTechnicalTags(prev => 
      prev.includes(tagId) 
        ? prev.filter(t => t !== tagId)
        : [...prev, tagId]
    );
  };

  const handleSubmit = async () => {
    if (quantity <= 0) {
      toast.error(`Enter valid ${isFO ? 'lots' : 'quantity'}`);
      return;
    }

    if (!isEditMode && !tradeReason) {
      setReasonError(true);
      toast.error('Select a reason for this trade');
      return;
    }


    setIsSubmitting(true);
    try {
      if (isEditMode) {
        // ── UPDATE existing OPEN order ──
        const allTags = [...(tradeReason ? [tradeReason] : []), ...technicalTags];
        const res = await axios.patch(`${API_URL}/trade/orders/${editOrder.trade_id || editOrder.order_id}`, {
          limit_price: limitPrice ? parseFloat(limitPrice) : undefined,
          stop_loss_price: stopLoss ? parseFloat(stopLoss) : undefined,
          target_price: target ? parseFloat(target) : undefined,
          quantity: unitQuantity,
          ...(isFO ? { lot_qty: quantity } : {}),
          notes: journalNotes || undefined,
          strategy_tags: allTags.length > 0 ? allTags : undefined,
        });
        toast.success(`Order updated — new limit ${formatPrice(limitPrice)}`);
        if (onOrderUpdated) onOrderUpdated(res.data);
        onClose();
      } else {
        // ── PLACE new order ──
        const _deSession = (() => {
          try {
            const s = localStorage.getItem('de_session');
            if (!s) return null;
            const p = JSON.parse(s);
            return (Date.now() - new Date(p.set_at).getTime()) < 4 * 60 * 60 * 1000 ? p : null;
          } catch { return null; }
        })();
        const _checklist = {
          trade_reason: tradeReason,
          confidence,
          journal_notes: journalNotes,
          ...(_deSession ? { decision_engine: _deSession } : {}),
        };
        await axios.post(`${API_URL}/trade/order`, {
          symbol: stock.symbol,
          order_type: type,
          quantity: unitQuantity,
          order_variant: orderMode === 'LIMIT' ? 'LIMIT' : (productType === 'INTRADAY' ? 'INTRADAY' : 'MARKET'),
          product_type: productType,
          limit_price: orderMode === 'LIMIT' ? parseFloat(limitPrice) : null,
          stop_loss_price: stopLoss ? parseFloat(stopLoss) : null,
          target_price: target ? parseFloat(target) : null,
          strategy_tags: technicalTags,
          checklist: _checklist,
          source: 'MANUAL',
          ...(isFO ? { contract_id: stock.contract_id, lot_qty: quantity } : {}),
        });
        toast.success(
          isFO
            ? `${type} order placed for ${quantity} lot${quantity > 1 ? 's' : ''} of ${displaySymbol}`
            : `${type} order placed for ${quantity} shares of ${displaySymbol}`
        );
        onClose();
      }
    } catch (error) {
      const msg = error?.response?.data?.detail || (isEditMode ? 'Failed to update order' : 'Failed to place order');
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const buyColor = '#6366F1';
  const sellColor = '#dc2626';
  const activeColor = type === 'BUY' ? buyColor : sellColor;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
      role="dialog" aria-modal="true" aria-label={`${type} order for ${stock?.symbol?.replace('.NS','')}`}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div
        className="relative w-full sm:max-w-md mx-auto rounded-t-2xl sm:rounded-2xl overflow-hidden shadow-2xl max-h-[92vh] flex flex-col"
        style={{ backgroundColor: colors.background, borderTop: `3px solid ${activeColor}` }}
        data-testid="order-modal"
        data-tour="order-ticket"
      >
        {/* ── HEADER ── */}
        <div className="flex items-center justify-between px-4 py-3 flex-shrink-0" style={{ borderBottom: `1px solid ${colors.border}` }}>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-base font-bold tracking-tight" style={{ color: colors.primaryText }}>{displaySymbol}</h2>
              {isEditMode && (
                <span className="text-[11px] px-1.5 py-0.5 rounded-full font-semibold uppercase tracking-wide"
                  style={{ backgroundColor: activeColor + '20', color: activeColor }}>Modify</span>
              )}
            </div>
            <p className="text-[12px]" style={{ color: colors.secondaryText }}>{stock.company_name}</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-sm font-bold" style={{ color: colors.primaryText }}>{formatPrice(currentPrice)}</p>
              {stock.change_pct != null && (
                <p className="text-[11px] font-semibold" style={{ color: stock.change_pct >= 0 ? colors.profit : colors.loss }}>
                  {stock.change_pct >= 0 ? '▲' : '▼'} {Math.abs(stock.change_pct)?.toFixed(2)}%
                </p>
              )}
            </div>
            <button onClick={onClose} aria-label="Close" className="w-7 h-7 rounded-full flex items-center justify-center" style={{ backgroundColor: colors.cardBg }}>
              <X className="w-4 h-4" style={{ color: colors.secondaryText }} />
            </button>
          </div>
        </div>

        {/* ── SCROLLABLE BODY ── */}
        <div className="overflow-y-auto flex-1 px-3 py-3 space-y-3">

          {/* BUY / SELL — full width */}
          <div className="flex rounded-xl overflow-hidden" style={{ border: `1px solid ${activeColor}50`, opacity: isEditMode ? 0.7 : 1 }}>
            <button
              onClick={() => !isEditMode && setType('BUY')}
              className="flex-1 py-2.5 text-sm font-bold transition-all"
              style={{ backgroundColor: type === 'BUY' ? buyColor : 'transparent', color: type === 'BUY' ? '#fff' : colors.secondaryText, cursor: isEditMode ? 'default' : 'pointer' }}
              data-testid="order-buy-tab"
            >BUY</button>
            <button
              onClick={() => !isEditMode && setType('SELL')}
              className="flex-1 py-2.5 text-sm font-bold transition-all"
              style={{ backgroundColor: type === 'SELL' ? sellColor : 'transparent', color: type === 'SELL' ? '#fff' : colors.secondaryText, cursor: isEditMode ? 'default' : 'pointer' }}
              data-testid="order-sell-tab"
            >SELL</button>
          </div>

          {/* INTRADAY / SWING */}
          <div data-tour="order-product-type" className="flex rounded-xl overflow-hidden" style={{ border: `1px solid ${colors.border}` }}>
            {[
              { id: 'INTRADAY', label: isFO ? 'Intraday' : 'Intraday', note: 'Same day' },
              { id: 'SWING',    label: isFO ? 'Carry'    : 'Swing',     note: 'Overnight' },
            ].map((p) => (
              <button key={p.id} onClick={() => setProductType(p.id)}
                className="flex-1 py-2 transition-all"
                style={{ backgroundColor: productType === p.id ? (p.id === 'INTRADAY' ? '#7c3aed' : activeColor) : 'transparent', color: productType === p.id ? '#fff' : colors.secondaryText }}
              >
                <span className="block text-[11px] font-bold">{p.label}</span>
                <span className="block text-[10px] opacity-60">{p.note}</span>
              </button>
            ))}
          </div>

          {/* Intraday warning */}
          {productType === 'INTRADAY' && (
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg" style={{ backgroundColor: '#7c3aed18', border: '1px solid #7c3aed30' }}>
              <span className="text-[11px]" style={{ color: '#a78bfa' }}>⚠ Intraday only executes 9:15 AM – 3:30 PM IST. Must close same day.</span>
            </div>
          )}

          {/* ORDER MODE + QTY */}
          <div data-tour="order-mode-qty" className="flex items-center gap-2">
            <div className="flex rounded-xl overflow-hidden flex-1" style={{ border: `1px solid ${colors.border}` }}>
              {['MARKET', 'LIMIT'].map((mode) => (
                <button key={mode} onClick={() => setOrderMode(mode)}
                  className="flex-1 py-2 text-xs font-semibold transition-all"
                  data-tour="order-mode-btn"
                  style={{ backgroundColor: orderMode === mode ? activeColor : 'transparent', color: orderMode === mode ? '#fff' : colors.secondaryText }}
                >{mode}</button>
              ))}
            </div>
            <div className="flex items-center gap-1">
              <span className="text-[11px] font-medium mr-1" style={{ color: colors.secondaryText }}>{quantityLabel}</span>
              <button onClick={() => setQuantity(Math.max(1, quantity - 1))}
                className="w-8 h-8 rounded-lg font-bold text-base flex items-center justify-center"
                style={{ backgroundColor: colors.cardBg, color: colors.primaryText }}>−</button>
              <input type="number" value={quantity}
                onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                className="w-12 text-center py-1.5 rounded-lg font-bold text-sm"
                style={{ backgroundColor: colors.cardBg, color: colors.primaryText, border: `1px solid ${colors.border}` }}
                data-testid="order-quantity-input" />
              <button onClick={() => setQuantity(quantity + 1)}
                className="w-8 h-8 rounded-lg font-bold text-base flex items-center justify-center"
                style={{ backgroundColor: colors.cardBg, color: colors.primaryText }}>+</button>
            </div>
          </div>
          {isFO && (
            <p className="text-[11px] text-right" style={{ color: colors.secondaryText }}>
              {unitQuantity} units ({quantity} lot{quantity > 1 ? 's' : ''} × {lotSize})
            </p>
          )}

          {/* Limit Price */}
          {orderMode === 'LIMIT' && (
            <input type="number" value={limitPrice} onChange={(e) => setLimitPrice(e.target.value)}
              className="w-full py-2 px-3 rounded-xl text-sm font-medium"
              style={{ backgroundColor: colors.cardBg, color: colors.primaryText, border: `1px solid ${colors.border}` }}
              placeholder="Limit Price (₹)" />
          )}

          {/* SL + TARGET side by side with labels */}
          <div data-tour="order-sl-target" className="grid grid-cols-2 gap-2">
            <div>
              <p className="text-[11px] font-semibold mb-1" style={{ color: productType === 'INTRADAY' && type === 'BUY' && !stopLoss ? '#ef4444' : '#f97316' }}>
                Stop Loss {productType === 'INTRADAY' ? '(required)' : '(recommended)'}
              </p>
              <input type="text" inputMode="decimal" value={stopLoss}
                onChange={(e) => { const v = e.target.value; if (v === '' || /^\d*\.?\d*$/.test(v)) setStopLoss(v); }}
                className="w-full py-2 px-3 rounded-xl text-sm font-medium"
                style={{ backgroundColor: colors.cardBg, color: colors.primaryText, border: `1px solid ${stopLoss ? '#ef444440' : productType === 'INTRADAY' && type === 'BUY' ? '#ef4444' : colors.border}` }}
                placeholder="₹ 0.00" />
            </div>
            <div>
              <p className="text-[11px] font-semibold mb-1" style={{ color: '#16C784' }}>Target</p>
              <input type="text" inputMode="decimal" value={target}
                onChange={(e) => { const v = e.target.value; if (v === '' || /^\d*\.?\d*$/.test(v)) setTarget(v); }}
                className="w-full py-2 px-3 rounded-xl text-sm font-medium"
                style={{ backgroundColor: colors.cardBg, color: colors.primaryText, border: `1px solid ${target ? '#16C78440' : colors.border}` }}
                placeholder="₹ 0.00" />
            </div>
          </div>

          {/* ── SECTION: WHY THIS TRADE ── */}
          <div data-tour="order-journal-reason" style={{ borderTop: `1px solid ${colors.border}`, paddingTop: 10 }}>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-bold uppercase tracking-wide" style={{ color: colors.primaryText }}>
                Why this Trade? <span style={{ color: colors.loss }}>*</span>
              </p>
              {reasonError && <span className="text-[11px] font-semibold" style={{ color: colors.loss }}>Pick one to continue</span>}
            </div>
            <div className="grid grid-cols-4 gap-1.5">
              {TRADE_REASONS.map((reason) => (
                <button key={reason.id}
                  onClick={() => { setTradeReason(reason.id); setReasonError(false); }}
                  className="py-1.5 rounded-xl text-[11px] font-semibold transition-all text-center"
                  style={{
                    backgroundColor: tradeReason === reason.id ? activeColor : colors.cardBg,
                    color: tradeReason === reason.id ? '#fff' : colors.secondaryText,
                    border: `1px solid ${tradeReason === reason.id ? activeColor : reasonError && !tradeReason ? colors.loss : 'transparent'}`,
                    transform: tradeReason === reason.id ? 'scale(1.04)' : 'scale(1)',
                  }}
                  data-testid={`reason-${reason.id}`}
                >
                  <span className="block text-base leading-tight">{reason.icon}</span>
                  <span className="block leading-tight mt-0.5">{reason.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* ── SECTION: TECHNICAL TAGS ── */}
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wide mb-1.5" style={{ color: colors.secondaryText }}>
              Technical Tags <span className="font-normal normal-case tracking-normal">(optional)</span>
            </p>
            <div className="flex flex-wrap gap-1.5">
              {TECHNICAL_TAGS.map((tag) => (
                <button key={tag.id} onClick={() => toggleTag(tag.id)}
                  className="px-2.5 py-1 rounded-full text-[11px] font-semibold transition-all"
                  style={{
                    backgroundColor: technicalTags.includes(tag.id) ? `${activeColor}25` : colors.cardBg,
                    color: technicalTags.includes(tag.id) ? activeColor : colors.secondaryText,
                    border: `1px solid ${technicalTags.includes(tag.id) ? activeColor + '60' : 'transparent'}`,
                  }}
                  data-testid={`tag-${tag.id}`}
                >{tag.label}</button>
              ))}
            </div>
          </div>

          {/* ── SECTION: CONFIDENCE ── */}
          {(() => {
            const CONF_LABELS = {
              1:  { label: 'No edge — why are you trading?',      color: '#ef4444' },
              2:  { label: 'Very low — gut feeling only',          color: '#ef4444' },
              3:  { label: 'Weak — not fully convinced',           color: '#f97316' },
              4:  { label: 'Below average — some doubt',           color: '#f97316' },
              5:  { label: 'Moderate — setup is okay',             color: '#f59e0b' },
              6:  { label: 'Decent — most boxes ticked',           color: '#f59e0b' },
              7:  { label: 'Good — solid setup',                   color: '#84cc16' },
              8:  { label: 'Strong — clear thesis',                color: '#22c55e' },
              9:  { label: 'Very high — high conviction',          color: '#16C784' },
              10: { label: 'Max conviction — all signals aligned', color: '#16C784' },
            };
            const conf = CONF_LABELS[confidence];
            const pct = ((confidence - 1) / 9) * 100;
            const trackGradient = `linear-gradient(to right, #ef4444 0%, #f97316 30%, #f59e0b 50%, #84cc16 70%, #16C784 100%)`;
            return (
              <div data-tour="order-confidence" className="rounded-xl p-3" style={{ backgroundColor: colors.cardBg, border: `1px solid ${conf.color}30` }}>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[11px] font-bold uppercase tracking-wide" style={{ color: colors.secondaryText }}>Confidence</p>
                  <span className="text-sm font-black px-2.5 py-0.5 rounded-full" style={{ backgroundColor: `${conf.color}20`, color: conf.color }}>{confidence}/10</span>
                </div>
                {/* Slider */}
                <div className="relative mb-3" style={{ paddingBottom: 2 }}>
                  {/* Gradient track background */}
                  <div className="w-full rounded-full" style={{ height: 8, background: trackGradient, opacity: 0.25 }} />
                  {/* Filled portion */}
                  <div className="absolute top-0 left-0 rounded-full" style={{ height: 8, width: `${pct}%`, background: trackGradient, transition: 'width 0.1s ease' }} />
                  {/* Native input on top — transparent, full coverage */}
                  <input
                    type="range" min="1" max="10" value={confidence}
                    onChange={(e) => setConfidence(parseInt(e.target.value))}
                    data-testid="confidence-slider"
                    style={{
                      position: 'absolute', top: -6, left: 0,
                      width: '100%', height: 20,
                      opacity: 0, cursor: 'pointer', margin: 0, padding: 0,
                    }}
                  />
                  {/* Custom thumb */}
                  <div
                    className="absolute rounded-full shadow-lg"
                    style={{
                      width: 20, height: 20,
                      top: -6,
                      left: `calc(${pct}% - ${pct * 0.2}px)`,
                      backgroundColor: conf.color,
                      border: '3px solid #fff',
                      boxShadow: `0 0 8px ${conf.color}80`,
                      transition: 'left 0.1s ease, background-color 0.2s ease',
                      pointerEvents: 'none',
                    }}
                  />
                </div>
                {/* Tick marks */}
                <div className="flex justify-between mb-2 px-0.5">
                  {Array.from({ length: 10 }, (_, i) => i + 1).map((i) => (
                    <span key={i} className="text-[9px] font-semibold" style={{ color: i === confidence ? conf.color : colors.secondaryText, opacity: i === confidence ? 1 : 0.4 }}>{i}</span>
                  ))}
                </div>
                {/* Live label */}
                <p className="text-[12px] font-semibold text-center" style={{ color: conf.color }}>{conf.label}</p>
              </div>
            );
          })()}

          {/* ── SECTION: JOURNAL NOTES ── */}
          <div className="rounded-xl p-2.5" style={{ background: 'linear-gradient(135deg, rgba(251,191,36,0.10) 0%, rgba(251,191,36,0.04) 100%)', border: '1px solid rgba(251,191,36,0.28)' }}>
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-[11px] font-bold uppercase tracking-wide" style={{ color: '#fbbf24' }}>✍ Journal Notes</p>
              <span className="text-[10px] font-medium" style={{ color: confidence >= 7 ? colors.profit : confidence <= 3 ? colors.loss : 'rgba(251,191,36,0.7)' }}>
                {confidence >= 7 ? 'What\'s your thesis?' : confidence <= 3 ? 'What\'s your edge here?' : 'What made you take this trade?'}
              </span>
            </div>
            <textarea value={journalNotes} onChange={(e) => setJournalNotes(e.target.value)}
              className="w-full px-3 py-2 rounded-lg text-xs resize-none"
              rows={2}
              style={{ background: 'rgba(251,191,36,0.05)', color: colors.primaryText, border: '1px solid rgba(251,191,36,0.18)' }}
              placeholder="Add notes about why you took this trade..."
              data-testid="journal-notes" />
          </div>

          {/* ── ORDER SUMMARY ── */}
          <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${activeColor}30` }}>
            <div className="px-3 py-2" style={{ backgroundColor: `${activeColor}12` }}>
              <p className="text-[11px] font-bold uppercase tracking-wide" style={{ color: activeColor }}>Order Summary</p>
            </div>
            <div className="px-3 py-2 space-y-1" style={{ backgroundColor: colors.cardBg }}>
              <div className="flex justify-between text-xs">
                <span style={{ color: colors.secondaryText }}>{isFO ? 'Price × Units' : 'Price × Qty'}</span>
                <span style={{ color: colors.primaryText }}>{formatPrice(currentPrice)} × {unitQuantity}</span>
              </div>
              {isFO && (
                <div className="flex justify-between text-xs">
                  <span style={{ color: colors.secondaryText }}>Lots × Lot Size</span>
                  <span style={{ color: colors.primaryText }}>{quantity} × {lotSize}</span>
                </div>
              )}
              {isFuture && (
                <div className="flex justify-between text-xs">
                  <span style={{ color: colors.secondaryText }}>Notional</span>
                  <span style={{ color: colors.secondaryText }}>{formatPrice(totalValue)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm font-bold pt-1.5" style={{ borderTop: `1px solid ${colors.border}` }}>
                <span style={{ color: colors.primaryText }}>{isFuture ? 'Margin (10%)' : 'Total'}</span>
                <span style={{ color: activeColor }}>{formatPrice(capitalRequired)}</span>
              </div>
              {isFuture && (
                <p className="text-[10px]" style={{ color: colors.secondaryText }}>Real NSE margin ~15% — we use 10% so you can practise</p>
              )}
            </div>
          </div>

          {/* ── RISK PANEL ── */}
          {recommendedMaxPct ? (
            <div data-tour="order-risk-panel" className="rounded-xl overflow-hidden" style={{ border: `1px solid ${riskSeverity === 'danger' ? '#ef444450' : colors.border}` }}>
              <div className="flex items-center justify-between px-3 py-2" style={{ backgroundColor: riskSeverity === 'danger' ? '#ef444415' : riskSeverity === 'warn' ? '#f9731610' : `${colors.accent}10` }}>
                <span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: riskSeverity === 'danger' ? '#ef4444' : riskSeverity === 'warn' ? '#f97316' : colors.secondaryText }}>
                  Loss Risk Check
                </span>
                <button onClick={() => setShowRiskPopup(true)} className="p-0.5" style={{ color: colors.secondaryText }}>
                  <Info className="w-3 h-3" />
                </button>
              </div>
              <div className="px-3 py-2 space-y-1.5" style={{ backgroundColor: colors.cardBg }}>
                {foProfileWarning && (
                  <div className="rounded-lg px-2.5 py-1.5" style={{ backgroundColor: '#f9731618' }}>
                    <p className="text-[12px] font-semibold" style={{ color: '#f97316' }}>⚠ F&O not recommended for your profile</p>
                    <p className="text-[11px] mt-0.5" style={{ color: '#f97316' }}>Your <span className="font-semibold">{profileType}</span> profile is calibrated for equity.</p>
                  </div>
                )}
                {isOption && impliedLossAmount != null && (
                  <div>
                    <div className="flex justify-between text-[11px] mb-1">
                      <span style={{ color: colors.secondaryText }}>Premium at risk: ₹{impliedLossAmount?.toFixed(0)} ({impliedLossPct?.toFixed(2)}%)</span>
                      <span style={{ color: colors.secondaryText }}>Limit: {recommendedMaxPct}%</span>
                    </div>
                    <div className="w-full rounded-full h-1.5" style={{ backgroundColor: colors.border }}>
                      <div className="h-1.5 rounded-full transition-all" style={{ width: `${riskBarWidth}%`, backgroundColor: riskBarColor }} />
                    </div>
                    <p className="text-[11px] mt-1 font-semibold" style={{ color: riskBarColor }}>
                      {riskSeverity === 'ok' ? `✓ Within your ₹${recommendedMaxLoss?.toFixed(0)} limit` : riskSeverity === 'warn' ? `⚠ Exceeds recommended risk limit` : `⛔ Well above your ${recommendedMaxPct}% limit`}
                    </p>
                  </div>
                )}
                {isFuture && notionalExposure != null && (
                  <p className="text-[11px]" style={{ color: colors.secondaryText }}>
                    Notional: <span className="font-semibold" style={{ color: colors.primaryText }}>₹{notionalExposure.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                    {notionalMultiple != null && <span style={{ color: notionalMultiple > 1 ? '#f97316' : colors.secondaryText }}> ({notionalMultiple.toFixed(1)}× capital)</span>}
                  </p>
                )}
                {!isOption && riskSeverity === 'no-sl' && (
                  <div>
                    <p className="text-[11px]" style={{ color: colors.secondaryText }}>Risk cannot be verified without a stop loss.</p>
                    {suggestedSL && suggestedSL > 0 && (
                      <p className="text-[11px] mt-1" style={{ color: '#f97316' }}>
                        To stay within ₹{recommendedMaxLoss?.toFixed(0)}, set SL {isBuy ? 'below' : 'above'} ₹{suggestedSL.toFixed(2)}
                      </p>
                    )}
                  </div>
                )}
                {!isOption && riskSeverity && riskSeverity !== 'no-sl' && (
                  <div>
                    <div className="flex justify-between text-[11px] mb-1">
                      <span style={{ color: colors.secondaryText }}>Max loss if SL hits: ₹{impliedLossAmount?.toFixed(0)} ({impliedLossPct?.toFixed(2)}%)</span>
                      <span style={{ color: colors.secondaryText }}>{recommendedMaxPct}%</span>
                    </div>
                    <div className="w-full rounded-full h-1.5" style={{ backgroundColor: colors.border }}>
                      <div className="h-1.5 rounded-full transition-all" style={{ width: `${riskBarWidth}%`, backgroundColor: riskBarColor }} />
                    </div>
                    <p className="text-[11px] mt-1 font-semibold" style={{ color: riskBarColor }}>
                      {riskSeverity === 'ok' ? `✓ ₹${impliedLossAmount?.toFixed(0)} — within your ₹${recommendedMaxLoss?.toFixed(0)} limit` : riskSeverity === 'warn' ? `⚠ Exceeds recommended limit if SL hits` : `⛔ Well above your ${recommendedMaxPct}% limit`}
                    </p>
                  </div>
                )}
                {styleMismatch && (
                  <p className="text-[11px]" style={{ color: '#f97316' }}>⚠ Your profile recommends {riskProfile.recommended_trade_style} — not Intraday</p>
                )}
                <div className="flex justify-between text-[11px]">
                  <span style={{ color: colors.secondaryText }}>Trades today: {todayTradeCount} / {riskProfile.recommended_max_trades}</span>
                  {atTradeLimit && <span className="font-semibold" style={{ color: '#ef4444' }}>⚠ At daily limit</span>}
                </div>
              </div>
            </div>
          ) : (
            <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${colors.border}` }}>
              <div className="flex items-center justify-between px-3 py-2" style={{ backgroundColor: `${colors.accent}10` }}>
                <span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: colors.secondaryText }}>Loss Risk Check</span>
                <Info className="w-3 h-3" style={{ color: colors.secondaryText }} />
              </div>
              <div className="px-3 py-2 space-y-1" style={{ backgroundColor: colors.cardBg }}>
                <p className="text-xs" style={{ color: colors.primaryText }}>Complete your risk profile to see personalized guidance.</p>
                <p className="text-[11px]" style={{ color: colors.secondaryText }}>You can still place the order.</p>
              </div>
            </div>
          )}

          {/* Risk Info Popup */}
          {showRiskPopup && (
            <div className="fixed inset-0 z-[200] flex items-end justify-center" onClick={() => setShowRiskPopup(false)}>
              <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
              <div className="relative w-full max-w-sm rounded-t-2xl p-5 space-y-3 z-10" style={{ background: `linear-gradient(160deg, #1e1f2e 0%, #13141a 60%, #1a1230 100%)`, borderTop: `3px solid ${activeColor}`, boxShadow: `0 -8px 40px ${activeColor}20` }} onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-bold" style={{ color: colors.primaryText }}>What does this mean?</p>
                  <button onClick={() => setShowRiskPopup(false)}><X className="w-4 h-4" style={{ color: colors.secondaryText }} /></button>
                </div>
                <p className="text-xs" style={{ color: colors.secondaryText }}>
                  Your <span className="font-semibold" style={{ color: colors.primaryText }}>{riskProfile.risk_profile_type}</span> profile recommends keeping max loss per trade within <span className="font-semibold" style={{ color: colors.primaryText }}>{recommendedMaxPct}%</span> of your capital.
                </p>
                {isOption && <p className="text-xs" style={{ color: '#f97316' }}>For options, risk is the <span className="font-semibold">full premium paid</span> — options can lose all value on a 1–2% adverse move.</p>}
                {isFuture && <p className="text-xs" style={{ color: '#f97316' }}>For futures, risk is SL-based delta. Notional exposure is far higher — use a strict stop loss.</p>}
                <div className="rounded-lg p-3 space-y-1" style={{ backgroundColor: colors.inputBg }}>
                  <p className="text-[13px] font-semibold" style={{ color: colors.primaryText }}>At {formatPrice(portfolioBalance, 0)} active capital:</p>
                  <p className="text-xs" style={{ color: colors.secondaryText }}>Max loss recommended: ≤ <span className="font-bold" style={{ color: '#16C784' }}>{formatPrice(recommendedMaxLoss || 0, 0)}</span></p>
                  {impliedLossAmount != null && <p className="text-xs" style={{ color: colors.secondaryText }}>{isOption ? 'Premium at risk' : 'This trade\'s max loss'}: <span className="font-bold" style={{ color: riskBarColor }}>{formatPrice(impliedLossAmount, 0)}</span> ({impliedLossPct?.toFixed(2)}%)</p>}
                  {notionalExposure != null && <p className="text-xs" style={{ color: colors.secondaryText }}>Futures notional: <span className="font-bold" style={{ color: '#f97316' }}>{formatPrice(notionalExposure, 0)}</span> ({notionalMultiple?.toFixed(1)}× capital)</p>}
                </div>
                <p className="text-[12px]" style={{ color: colors.secondaryText }}>Advisory only — you can still place the order.</p>
              </div>
            </div>
          )}

          {/* Nyx Quality Score */}
          {qualityLoading && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl" style={{ backgroundColor: colors.cardBg, border: `1px solid ${colors.border}` }}>
              <Sparkles className="w-3.5 h-3.5 animate-pulse" style={{ color: colors.accent }} />
              <span className="text-xs" style={{ color: colors.secondaryText }}>Nyx is analyzing…</span>
            </div>
          )}
          {qualityScore && !qualityLoading && (
            <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${colors.border}` }}>
              <div className="flex items-center justify-between px-3 py-2" style={{ backgroundColor: `${colors.accent}10` }}>
                <div className="flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3" style={{ color: colors.accent }} />
                  <span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: colors.secondaryText }}>Trade Quality</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold" style={{ color: qualityScore.score <= 4 ? '#dc2626' : qualityScore.score <= 6 ? '#d97706' : '#16a34a' }}>{qualityScore.score}/10</span>
                  <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold" style={{ backgroundColor: qualityScore.suggested_action === 'Proceed' ? '#16a34a22' : '#dc262622', color: qualityScore.suggested_action === 'Proceed' ? '#16a34a' : '#dc2626' }}>{qualityScore.suggested_action}</span>
                </div>
              </div>
              {qualityScore.issues?.length > 0 && (
                <div className="px-3 py-2 space-y-0.5" style={{ backgroundColor: colors.cardBg }}>
                  {qualityScore.issues.map((issue, i) => (
                    <p key={i} className="text-[11px]" style={{ color: colors.secondaryText }}><span style={{ color: '#d97706' }}>▲</span> {issue}</p>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Demo notice */}
          <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg" style={{ backgroundColor: `${colors.accent}08` }}>
            <AlertTriangle className="w-3 h-3 flex-shrink-0" style={{ color: colors.secondaryText }} />
            <span className="text-[11px]" style={{ color: colors.secondaryText }}>Paper trading — no real money involved.</span>
          </div>
        </div>

        {/* ── PLACE BUTTON ── */}
        <div data-tour="order-place-btn" className="px-3 pb-4 pt-2 flex-shrink-0" style={{ borderTop: `1px solid ${colors.border}` }}>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || isTourRunning}
            className="w-full py-3 rounded-xl font-bold text-white text-sm tracking-wide transition-all disabled:opacity-50"
            style={{ background: `linear-gradient(135deg, ${activeColor} 0%, ${activeColor}cc 100%)`, boxShadow: `0 4px 20px ${activeColor}40` }}
            data-testid="order-submit-btn"
          >
            {isTourRunning
              ? 'Finish the tour to place orders'
              : isSubmitting
              ? (isEditMode ? 'Updating…' : 'Placing…')
              : isEditMode
              ? 'Update Order'
              : isFO
              ? `${type} ${quantity} Lot${quantity > 1 ? 's' : ''}`
              : `${type} ${quantity} Share${quantity > 1 ? 's' : ''}`
            }
          </button>
        </div>
      </div>
    </div>
  );
}

export default OrderModal;
